import React, { useMemo } from 'react';

import { TopTemplatesHeader } from 'src/components/community/TopTemplatesHeader';
import { EmptyState } from 'src/components/EmptyState';
import { WorkflowIcon } from 'src/components/icons/WorkflowIcon';
import type { Apollo } from 'src/gql/generated';
import { learnHowToContributeUrl, openLink } from 'src/helpers';
import { useCommunityWorkflows, useSelectedCommunityCategory } from 'src/hooks/workflow/community';
import { makeStyles } from 'tss-react/mui';

import type { CommunityWorkflowCardWorkflow } from './CommunityWorkflowCard';
import { CommunityWorkflowCard, CommunityWorkflowLoadingCard } from './CommunityWorkflowCard';

const useStyles = makeStyles()(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  icon: {
    height: 32,
    width: 32,
  },
  topWorkflowsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
}));

export function CommunityWorkflowBody(): JSX.Element {
  const { selectedCategory } = useSelectedCommunityCategory();
  const { communityWorkflows, loading } = useCommunityWorkflows({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const sortedFilteredWorkflows = useMemo(() => {
    const sortedWorkflows = [...communityWorkflows].sort(
      (a, b) => b.numTimesImported - a.numTimesImported
    );
    if (!selectedCategory) return sortedWorkflows;

    return sortedWorkflows.filter(w =>
      w.communityCategories.some(c => c.id === selectedCategory.id)
    );
  }, [communityWorkflows, selectedCategory]);

  if (!sortedFilteredWorkflows.length)
    return <EmptyCommunityWorkflows selectedCategory={selectedCategory} />;

  return <TopWorkflows loading={loading} workflows={sortedFilteredWorkflows} />;
}

interface TopWorkflowsProps {
  loading: boolean;
  workflows: CommunityWorkflowCardWorkflow[];
}

export function TopWorkflows({ loading, workflows }: TopWorkflowsProps): JSX.Element {
  const { classes } = useStyles();
  if (loading)
    return (
      <div className={classes.topWorkflowsContainer}>
        <TopTemplatesHeader variant="workflow" />
        <div className={classes.gridContainer}>
          {Array.from({ length: 3 }).map((_v, index) => {
            return <CommunityWorkflowLoadingCard key={index} />;
          })}
        </div>
      </div>
    );

  return (
    <div className={classes.topWorkflowsContainer}>
      <TopTemplatesHeader variant="workflow" />
      <div className={classes.gridContainer}>
        {workflows.map(workflow => (
          <CommunityWorkflowCard key={workflow.id} workflow={workflow} />
        ))}
      </div>
    </div>
  );
}

interface EmptyCommunityWorkflowsProps {
  selectedCategory: Pick<Apollo.CommunityCategory, 'title' | '__typename'> | null;
}

export function EmptyCommunityWorkflows({
  selectedCategory,
}: EmptyCommunityWorkflowsProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <EmptyState
      buttonProps={{ onClick: () => openLink(learnHowToContributeUrl) }}
      buttonText="Learn How to Contribute"
      icon={<WorkflowIcon className={classes.icon} />}
      title={selectedCategory ? `No ${selectedCategory.title} Workflows` : 'No Community Workflows'}
    />
  );
}
